import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Download,
  FileSpreadsheet,
  Calendar as CalendarIcon,
  Filter,
  X,
  RefreshCw,
  TrendingUp,
  CircleDollarSign,
  Receipt,
  Clock,
  AlertCircle,
  CheckCircle2,
  Search
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { format } from 'date-fns';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DistributionsPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  // State for History tab
  const [distributions, setDistributions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // State for Upcoming tab
  const [upcoming, setUpcoming] = useState([]);
  const [upcomingSummary, setUpcomingSummary] = useState(null);
  const [upcomingLoading, setUpcomingLoading] = useState(true);

  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchLoan, setSearchLoan] = useState('');
  const [dateRange, setDateRange] = useState({ from: null, to: null });

  // Fetch distribution history
  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.append('distribution_type', typeFilter);
      if (searchLoan) params.append('loan_reference', searchLoan);
      if (dateRange.from) params.append('start_date', dateRange.from.toISOString());
      if (dateRange.to) params.append('end_date', dateRange.to.toISOString());

      const [distRes, summaryRes, chartRes] = await Promise.all([
        axios.get(`${API_URL}/api/distributions?${params}`, { headers }),
        axios.get(`${API_URL}/api/distributions/summary`, { headers }),
        axios.get(`${API_URL}/api/distributions/monthly-chart`, { headers })
      ]);

      setDistributions(distRes.data.items || []);
      setSummary(summaryRes.data);
      setChartData(chartRes.data);
    } catch (error) {
      console.error('Failed to fetch distributions:', error);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, searchLoan, dateRange, token]);

  // Fetch upcoming distributions
  const fetchUpcoming = useCallback(async () => {
    setUpcomingLoading(true);
    try {
      const [upcomingRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/distributions/upcoming`, { headers }),
        axios.get(`${API_URL}/api/distributions/upcoming-summary`, { headers })
      ]);

      setUpcoming(upcomingRes.data);
      setUpcomingSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to fetch upcoming distributions:', error);
    } finally {
      setUpcomingLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHistory();
    fetchUpcoming();
  }, [fetchHistory, fetchUpcoming]);

  // Format helpers
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'R 0.00';
    return `R ${new Intl.NumberFormat('en-ZA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'interest': return 'bg-green-100 text-green-700 border-green-200';
      case 'capital': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'early_settlement': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'fee_rebate': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'interest': return 'Interest';
      case 'capital': return 'Capital';
      case 'early_settlement': return 'Early Settlement';
      case 'fee_rebate': return 'Fee Rebate';
      default: return type;
    }
  };

  const getConfidenceBadge = (confidence) => {
    switch (confidence) {
      case 'high':
        return <Badge className="bg-green-100 text-green-700 border-green-200 border">High</Badge>;
      case 'medium':
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200 border">Medium</Badge>;
      case 'low':
        return <Badge className="bg-red-100 text-red-700 border-red-200 border">Low</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700">Unknown</Badge>;
    }
  };

  const handleExportCSV = () => {
    window.open(`${API_URL}/api/distributions/export/csv`, '_blank');
  };

  const clearFilters = () => {
    setTypeFilter('all');
    setSearchLoan('');
    setDateRange({ from: null, to: null });
  };

  const hasFilters = typeFilter !== 'all' || searchLoan || dateRange.from || dateRange.to;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-[#212529] mb-2">{label}</p>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: entry.color }} />
              <span className="text-[#6C757D]">{entry.name}:</span>
              <span className="font-medium">{formatCurrency(entry.value)}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="distributions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#212529] font-['Manrope']">Distributions & Cashflows</h1>
          <p className="text-[#6C757D] mt-1">Track your investment returns and upcoming payments</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { fetchHistory(); fetchUpcoming(); }}
            className="border-gray-200"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs defaultValue="history" className="space-y-6">
        <TabsList className="bg-gray-100 p-1">
          <TabsTrigger value="history" className="data-[state=active]:bg-white" data-testid="tab-history">
            Distribution History
          </TabsTrigger>
          <TabsTrigger value="upcoming" className="data-[state=active]:bg-white" data-testid="tab-upcoming">
            Upcoming Distributions
          </TabsTrigger>
        </TabsList>

        {/* Distribution History Tab */}
        <TabsContent value="history" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-0 shadow-sm bg-gradient-to-br from-[#1D2D2A] to-[#315C52] text-white">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                    <CircleDollarSign className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm text-white/80">Total Distributions (YTD)</p>
                    <p className="text-2xl font-bold" data-testid="total-ytd">
                      {formatCurrency(summary?.total_ytd)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#18A37C]/10 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-[#18A37C]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#6C757D]">Total Distributions (All Time)</p>
                    <p className="text-2xl font-bold text-[#212529]" data-testid="total-all-time">
                      {formatCurrency(summary?.total_all_time)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Receipt className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-[#6C757D]">Withholding Tax (YTD)</p>
                    <p className="text-2xl font-bold text-[#212529]" data-testid="total-tax-ytd">
                      {formatCurrency(summary?.total_withholding_ytd)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Monthly Chart */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-['Manrope']">Monthly Distributions</CardTitle>
              <CardDescription>Interest vs Capital distributions over the last 12 months</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-[300px] w-full" />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" />
                    <XAxis 
                      dataKey="month_label" 
                      tick={{ fill: '#6C757D', fontSize: 12 }}
                    />
                    <YAxis 
                      tick={{ fill: '#6C757D', fontSize: 12 }}
                      tickFormatter={(value) => `R${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="interest" name="Interest" fill="#198754" stackId="a" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="capital" name="Capital" fill="#0D6EFD" stackId="a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Filters */}
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#ADB5BD]" />
                  <Input
                    placeholder="Search by loan reference..."
                    value={searchLoan}
                    onChange={(e) => setSearchLoan(e.target.value)}
                    className="pl-10 border-gray-200"
                    data-testid="search-loan"
                  />
                </div>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-full md:w-[180px] border-gray-200" data-testid="type-filter">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent className="z-50">
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="interest">Interest</SelectItem>
                    <SelectItem value="capital">Capital</SelectItem>
                    <SelectItem value="early_settlement">Early Settlement</SelectItem>
                    <SelectItem value="fee_rebate">Fee Rebate</SelectItem>
                  </SelectContent>
                </Select>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full md:w-[260px] justify-start text-left font-normal border-gray-200"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateRange.from ? (
                        dateRange.to ? (
                          <>
                            {format(dateRange.from, 'dd MMM')} - {format(dateRange.to, 'dd MMM yyyy')}
                          </>
                        ) : (
                          format(dateRange.from, 'dd MMM yyyy')
                        )
                      ) : (
                        <span className="text-muted-foreground">Date range</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-50" align="start">
                    <Calendar
                      initialFocus
                      mode="range"
                      selected={dateRange}
                      onSelect={setDateRange}
                      numberOfMonths={2}
                    />
                  </PopoverContent>
                </Popover>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportCSV}
                  className="border-gray-200"
                  data-testid="export-csv-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  CSV
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-gray-200"
                  data-testid="export-excel-btn"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Excel
                </Button>
                {hasFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-[#6C757D]">
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Distribution Table */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-4">
                  {[1, 2, 3, 4, 5].map(i => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : distributions.length === 0 ? (
                <div className="p-12 text-center">
                  <CircleDollarSign className="h-12 w-12 mx-auto text-[#ADB5BD] mb-4" />
                  <p className="text-[#6C757D]">No distributions found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Date</TableHead>
                        <TableHead>Loan Reference</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Gross Amount</TableHead>
                        <TableHead className="text-right">Withholding Tax</TableHead>
                        <TableHead className="text-right">Net Amount</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead>Reference</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {distributions.map((dist) => (
                        <TableRow key={dist.id} data-testid={`dist-row-${dist.id}`}>
                          <TableCell>{formatDate(dist.distribution_date)}</TableCell>
                          <TableCell className="font-medium text-[#1D2D2A]">
                            {dist.loan_reference || '-'}
                          </TableCell>
                          <TableCell>
                            <Badge className={`${getTypeColor(dist.distribution_type)} border`}>
                              {getTypeLabel(dist.distribution_type)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(dist.gross_amount)}
                          </TableCell>
                          <TableCell className="text-right text-red-600">
                            {dist.withholding_tax > 0 ? `-${formatCurrency(dist.withholding_tax)}` : '-'}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-green-600">
                            {formatCurrency(dist.net_amount)}
                          </TableCell>
                          <TableCell className="text-[#6C757D]">{dist.payment_method}</TableCell>
                          <TableCell className="text-xs text-[#6C757D] font-mono">
                            {dist.bank_reference || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Upcoming Distributions Tab */}
        <TabsContent value="upcoming" className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Clock className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-[#6C757D]">Next 30 Days</p>
                    <p className="text-2xl font-bold text-[#212529]" data-testid="upcoming-30">
                      {formatCurrency(upcomingSummary?.next_30_days)}
                    </p>
                    <p className="text-xs text-[#6C757D]">
                      {upcomingSummary?.payment_count_30 || 0} payments
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Clock className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-[#6C757D]">Next 60 Days</p>
                    <p className="text-2xl font-bold text-[#212529]" data-testid="upcoming-60">
                      {formatCurrency(upcomingSummary?.next_60_days)}
                    </p>
                    <p className="text-xs text-[#6C757D]">
                      {upcomingSummary?.payment_count_60 || 0} payments
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm bg-gradient-to-br from-[#1D2D2A] to-[#315C52] text-white">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-sm text-white/80">Next 90 Days</p>
                    <p className="text-2xl font-bold" data-testid="upcoming-90">
                      {formatCurrency(upcomingSummary?.next_90_days)}
                    </p>
                    <p className="text-xs text-white/70">
                      {upcomingSummary?.payment_count_90 || 0} payments
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Confidence Legend */}
          <Card className="border-0 shadow-sm">
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center gap-6">
                <span className="text-sm text-[#6C757D]">Confidence Level:</span>
                <div className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-700 border-green-200 border">High</Badge>
                  <span className="text-xs text-[#6C757D]">Scheduled & performing (fixed rate)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-amber-100 text-amber-700 border-amber-200 border">Medium</Badge>
                  <span className="text-xs text-[#6C757D]">Performing (variable rate)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-red-100 text-red-700 border-red-200 border">Low</Badge>
                  <span className="text-xs text-[#6C757D]">In arrears</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Table */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-['Manrope']">Projected Payments</CardTitle>
              <CardDescription>Expected distributions for the next 90 days based on repayment schedules</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {upcomingLoading ? (
                <div className="p-6 space-y-4">
                  {[1, 2, 3, 4, 5].map(i => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : upcoming.length === 0 ? (
                <div className="p-12 text-center">
                  <CheckCircle2 className="h-12 w-12 mx-auto text-[#ADB5BD] mb-4" />
                  <p className="text-[#6C757D]">No upcoming distributions in the next 90 days</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Expected Date</TableHead>
                        <TableHead>Loan Reference</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Expected Amount</TableHead>
                        <TableHead className="text-center">Confidence</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {upcoming.map((item) => (
                        <TableRow key={item.id} data-testid={`upcoming-row-${item.id}`}>
                          <TableCell>{formatDate(item.expected_date)}</TableCell>
                          <TableCell className="font-medium text-[#1D2D2A]">
                            {item.loan_reference}
                          </TableCell>
                          <TableCell>
                            <Badge className={`${getTypeColor(item.distribution_type)} border`}>
                              {getTypeLabel(item.distribution_type)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(item.expected_amount)}
                          </TableCell>
                          <TableCell className="text-center">
                            {getConfidenceBadge(item.confidence)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DistributionsPage;
